'use client';

import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'sf-support-fab-hidden';
const EVENT = 'sf-support:fab-hidden-changed';

/**
 * Persisted preference: e ascuns FAB-ul de suport sau nu?
 * Cand e ascuns, butonul de acces apare in footer-ul sidebar-ului stang.
 *
 * - Server: returneaza false (no-op pentru SSR — evita hydration mismatch)
 * - Client: citeste din localStorage la mount + sincronizeaza intre tab-uri
 *   prin storage event + intre componente prin custom event 'sf-support:fab-hidden-changed'
 */
export function useFabHidden() {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const initial = window.localStorage.getItem(STORAGE_KEY) === '1';
    setHidden(initial);

    function onStorage(e: StorageEvent) {
      if (e.key === STORAGE_KEY) setHidden(e.newValue === '1');
    }
    function onLocal() {
      setHidden(window.localStorage.getItem(STORAGE_KEY) === '1');
    }
    window.addEventListener('storage', onStorage);
    window.addEventListener(EVENT, onLocal);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(EVENT, onLocal);
    };
  }, []);

  const persist = useCallback((value: boolean) => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, value ? '1' : '0');
    // Notifica restul componentelor montate in acelasi tab
    window.dispatchEvent(new CustomEvent(EVENT));
  }, []);

  const hide = useCallback(() => {
    setHidden(true);
    persist(true);
  }, [persist]);

  const show = useCallback(() => {
    setHidden(false);
    persist(false);
  }, [persist]);

  const toggle = useCallback(() => {
    setHidden((prev) => {
      const next = !prev;
      persist(next);
      return next;
    });
  }, [persist]);

  return { hidden, hide, show, toggle };
}
